import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import eventService from "services/eventService";

import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";

import { useAuth } from "auth/AuthProvider";
import { canEdit } from "auth/roles";
import CreateEventDialog from "components/CreateEventDialog";

function formatLocation(loc) {
  return (typeof loc === "string" ? loc : "")?.trim() || "Lieu non spécifié";
}

function UpcomingEvents({ maxEvents = 5 }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const upcomingEvents = await eventService.getUpcomingEvents(maxEvents);
      setEvents(upcomingEvents);
      setError(null);
    } catch (err) {
      setError("Impossible de charger les événements");
    } finally {
      setLoading(false);
    }
  }, [maxEvents]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <Typography>Chargement des événements...</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Card sx={{ p: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h4" component="h2">
          Événements à venir
        </Typography>
        {canEdit(user) && (
          <Tooltip title="Ajouter un évènement">
            <IconButton aria-label="Ajouter" onClick={() => setOpenCreate(true)}>
              <AddCircleOutline />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {events.length === 0 ? (
        <Typography>Aucun événement à venir</Typography>
      ) : (
        <List>
          {events.map((event, index) => (
            <React.Fragment key={event.slug}>
              <ListItem
                button
                onClick={() => navigate(`/calendrier/${event.slug}`)}
                sx={{ cursor: "pointer" }}
              >
                <List disablePadding>
                  <ListItem disablePadding>
                    <Typography variant="h6">
                      Le {new Date(event.startDate).toLocaleDateString()}&nbsp;•&nbsp;
                      {formatLocation(event.locationManual)}
                    </Typography>
                  </ListItem>
                  <ListItem disablePadding>
                    <Typography variant="body2">{event.title}</Typography>
                  </ListItem>
                </List>
              </ListItem>
              {index < events.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      )}

      <CreateEventDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreated={() => {
          setOpenCreate(false);
          load();
        }}
      />
    </Card>
  );
}

UpcomingEvents.propTypes = {
  maxEvents: PropTypes.number,
};

export default UpcomingEvents;
